import {
  AccountTreeGroup,
  AccountTreeNode,
} from "../domain/entities/AccountTreeEntity";
import { GLMappingEntity } from "../domain/entities/GLMappingEntity";

export function formatAccountTypeLabel(value: string): string {
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function treeNodeToGLMappingEntity(node: AccountTreeNode): GLMappingEntity {
  return {
    account_id: node.account_id,
    gl_id: node.gl_id,
    gl_name: node.gl_name,
    account_type: node.account_type,
    full_path: node.full_path,
    is_active: "1",
    created_at: new Date().toISOString(),
  };
}

export function filterExcludedTreeNodes(
  nodes: AccountTreeNode[],
  excludeAccountIds?: ReadonlySet<string>,
): AccountTreeNode[] {
  if (!excludeAccountIds || excludeAccountIds.size === 0) {
    return nodes;
  }

  return nodes
    .filter((node) => !excludeAccountIds.has(node.account_id))
    .map((node) => ({
      ...node,
      children: filterExcludedTreeNodes(node.children ?? [], excludeAccountIds),
    }));
}

export function hasSelectedDescendant(
  node: AccountTreeNode,
  selectedId?: string,
): boolean {
  if (!selectedId) return false;
  if (node.account_id === selectedId) return true;
  return (node.children || []).some((child) =>
    hasSelectedDescendant(child, selectedId),
  );
}

export function findNodeByAccountId(
  groups: AccountTreeGroup[],
  accountId: string,
): AccountTreeNode | null {
  for (const group of groups) {
    const found = findNodeInTree(group.nodes, accountId);
    if (found) return found;
  }
  return null;
}

/**
 * Walk loaded tree groups and return "Parent > Child > Leaf" for accountId.
 * Matches the visible chart hierarchy (parent_id nesting), independent of gl_name.
 */
export function buildPathFromTree(
  groups: AccountTreeGroup[],
  accountId: string,
): string | null {
  for (const group of groups) {
    const segments = findPathSegmentsInNodes(group.nodes, accountId);
    if (segments?.length) {
      return segments.join(" > ");
    }
  }
  return null;
}

function findPathSegmentsInNodes(
  nodes: AccountTreeNode[],
  accountId: string,
  ancestors: string[] = [],
): string[] | null {
  for (const node of nodes) {
    const path = [...ancestors, node.gl_name];
    if (node.account_id === accountId) {
      return path;
    }
    if (node.children?.length) {
      const found = findPathSegmentsInNodes(node.children, accountId, path);
      if (found) return found;
    }
  }
  return null;
}

function findNodeInTree(
  nodes: AccountTreeNode[],
  accountId: string,
): AccountTreeNode | null {
  for (const node of nodes) {
    if (node.account_id === accountId) return node;
    if (node.children?.length) {
      const found = findNodeInTree(node.children, accountId);
      if (found) return found;
    }
  }
  return null;
}

function mergeTreesRecursive(
  existing: AccountTreeNode[],
  incoming: AccountTreeNode[],
): AccountTreeNode[] {
  const byId = new Map<string, AccountTreeNode>();

  for (const node of existing) {
    byId.set(node.account_id, {
      ...node,
      children: node.children ? [...node.children] : [],
    });
  }

  for (const node of incoming) {
    const current = byId.get(node.account_id);
    if (current) {
      current.children = mergeTreesRecursive(
        current.children || [],
        node.children || [],
      );
    } else {
      byId.set(node.account_id, {
        ...node,
        children: node.children ? [...node.children] : [],
      });
    }
  }

  const result: AccountTreeNode[] = [];
  const seen = new Set<string>();

  for (const node of existing) {
    const merged = byId.get(node.account_id);
    if (merged && !seen.has(node.account_id)) {
      result.push(merged);
      seen.add(node.account_id);
    }
  }

  for (const node of incoming) {
    if (!seen.has(node.account_id)) {
      const merged = byId.get(node.account_id);
      if (merged) {
        result.push(merged);
        seen.add(node.account_id);
      }
    }
  }

  return result;
}

export function mergeTreeGroups(
  existing: AccountTreeGroup[],
  incoming: AccountTreeGroup[],
): AccountTreeGroup[] {
  const groupMap = new Map<string, AccountTreeGroup>();

  for (const group of existing) {
    groupMap.set(group.account_type, {
      ...group,
      nodes: [...group.nodes],
    });
  }

  for (const group of incoming) {
    const current = groupMap.get(group.account_type);
    if (current) {
      groupMap.set(group.account_type, {
        ...current,
        total_leaves: Math.max(current.total_leaves, group.total_leaves),
        nodes: mergeTreesRecursive(current.nodes, group.nodes),
      });
    } else {
      groupMap.set(group.account_type, {
        ...group,
        nodes: [...group.nodes],
      });
    }
  }

  return Array.from(groupMap.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/** Replace nodes for one account_type group (browse mode lazy load). */
export function setGroupNodes(
  groups: AccountTreeGroup[],
  accountType: string,
  nodes: AccountTreeNode[],
): AccountTreeGroup[] {
  return groups.map((group) =>
    group.account_type === accountType ? { ...group, nodes } : group,
  );
}

export function flattenSelectableLeaves(
  groups: AccountTreeGroup[],
): AccountTreeNode[] {
  const leaves: AccountTreeNode[] = [];

  const walk = (nodes: AccountTreeNode[]) => {
    for (const node of nodes) {
      if (node.is_selectable) {
        leaves.push(node);
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  for (const group of groups) {
    walk(group.nodes);
  }

  return leaves;
}

/** Left padding in px for nested tree rows (matches Subframe mock). */
export function getTreeRowPaddingPx(depth: number): number {
  return 34 + depth * 24;
}

/** Full location breadcrumb for pinned selected account (type + in-tree path). */
export function buildSelectedAccountDisplayPath(
  accountTypeLabel: string | null | undefined,
  fullPath?: string,
  accountName?: string,
): string | null {
  if (!accountTypeLabel) {
    return fullPath && fullPath.includes(">") ? fullPath : null;
  }

  if (fullPath && fullPath.includes(">")) {
    if (fullPath.startsWith(`${accountTypeLabel} >`) || fullPath === accountTypeLabel) {
      return fullPath;
    }
    return `${accountTypeLabel} > ${fullPath}`;
  }

  if (accountName && accountName !== accountTypeLabel) {
    return `${accountTypeLabel} > ${accountName}`;
  }

  return accountTypeLabel;
}

/** Put the selected account's type group first in browse mode. */
export function orderGroupsWithSelectedFirst(
  groups: AccountTreeGroup[],
  selectedAccountType: string | null,
  searchActive: boolean,
): AccountTreeGroup[] {
  if (searchActive || !selectedAccountType) {
    return groups;
  }

  const selectedGroup = groups.find(
    (group) => group.account_type === selectedAccountType,
  );
  if (!selectedGroup) {
    return groups;
  }

  return [
    selectedGroup,
    ...groups.filter((group) => group.account_type !== selectedAccountType),
  ];
}
